import { DefaultLoader, LoaderContainer } from 'components/Loader';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import {
  setOrder,
  setPage,
  setSearch,
  setTags,
  setTake,
} from 'store/actions/admin/menteeLists';
import { StoreState } from 'store/reducers';
import useSWR from 'swr';
import GetOrderValue from 'types/GetOrderValue';
import Tags from 'types/Tags';
import { Action } from 'types/typeguards/reducerActions';
import User from 'types/User';
import { getIsMenteeStatus } from 'utils/menteeTags';
import getTagsQueryParams from 'utils/tagQueryParam';
import Head from './index-components/Head';
import MenteesFilters from './index-components/MenteeFilters';
import MenteesList from './index-components/MenteeList';

export const tagColumnNames = [
  'referee',
  'status',
  'jobSearch',
  'careerPlan',
  'studies',
  'entrepreneurship',
  'expertiseFields',
  'completedFormation',
  'validatedDiploma',
  'formationMatchingProject',
  'formationState',
  'intendedFormation',
  'intendedDiploma',
  'consideration',
  'hobby',
  'volunteerState',
] as const;

export type TagColumnNames = typeof tagColumnNames[number];

type RetrievedData = {
  mentees: User[] | undefined;
  count: number | undefined;
  pages: number | undefined;
  filteredCount: number | undefined;
};

export const sortableColumns = [
  'firstName',
  'lastName',
  'createdAt',
  'status',
] as const;
export type OrderValue = GetOrderValue<typeof sortableColumns[number]>;

type Props = {
  page: number;
  setPage: (page: number) => void;
  search: string;
  setSearch: (search: string) => void;
  tags: Tags<TagColumnNames>;
  setTags: (tag: Tags<TagColumnNames>) => void;
  take: number;
  setTake: (take: number) => void;
  order: OrderValue;
  setOrder: (order: OrderValue) => void;
};

type ContextType = RetrievedData & {
  loading: boolean;
} & Props;
export const MenteesContext = React.createContext({} as ContextType);
const { Provider } = MenteesContext;

const Mentees: FC<Props> = ({
  children,
  page,
  setPage,
  search,
  setSearch,
  tags,
  setTags,
  take,
  setTake,
  order,
  setOrder,
}) => {
  const { data, error } = useSWR<RetrievedData>(
    `/user/mentee/\
		?page=${page}&take=${take}\
		${search ? `&search=${search}` : ''}\
		${
      tags.status[0]
        ? tags.status
            .map((tag) =>
              getIsMenteeStatus(tag)
                ? `&menteeTags=${tag}`
                : `&mentorshipTags=${tag}`,
            )
            .join('')
        : ''
    }\
		${getTagsQueryParams<TagColumnNames>(tags, [
      'referee',
      'jobSearch',
      'careerPlan',
      'studies',
      'entrepreneurship',
      'expertiseFields',
      'completedFormation',
      'validatedDiploma',
      'formationMatchingProject',
      'formationState',
      'intendedFormation',
      'intendedDiploma',
      'consideration',
      'hobby',
      'volunteerState',
    ])}\
&order=${order}
		`,
  );

  const [retrievedData, setRetrievedData] = useState<RetrievedData>({
    mentees: undefined,
    count: undefined,
    pages: undefined,
    filteredCount: undefined,
  });
  useEffect(() => {
    if (data) {
      setRetrievedData(data);
    }
  }, [data]);

  // Reset page to 0 if the stored page is out of range
  useEffect(() => {
    if (data?.pages && data?.pages <= page) {
      setPage(0);
    }
  }, [data, page, setPage]);

  const value = useMemo(
    () => ({
      ...retrievedData,
      page,
      setPage,
      search,
      setSearch,
      tags,
      setTags,
      loading: !data,
      take,
      setTake,
      order,
      setOrder,
    }),
    [
      page,
      setPage,
      search,
      setSearch,
      tags,
      setTags,
      retrievedData,
      data,
      take,
      setTake,
      order,
      setOrder,
    ],
  );

  return retrievedData.mentees || error ? (
    <Provider value={value}>{children}</Provider>
  ) : (
    <LoaderContainer>
      <DefaultLoader size={1.5} />
    </LoaderContainer>
  );
};

const Usage: FC<Props> = (props) => {
  return (
    <Mentees {...props}>
      <Head />
      <MenteesFilters />
      <MenteesList />
    </Mentees>
  );
};

const mapStateToProps = (state: StoreState) => {
  return {
    page: state.admin.menteeLists.main.page,
    search: state.admin.menteeLists.main.search,
    tags: state.admin.menteeLists.main.tags,
    take: state.admin.menteeLists.main.take,
    order: state.admin.menteeLists.main.order,
  };
};

const mapDispatchToProps = (dispatch: (action: Action) => void) => {
  return {
    setPage: (page: number) => dispatch(setPage('main', page)),
    setSearch: (search: string) => dispatch(setSearch('main', search)),
    setTags: (tags: Tags<TagColumnNames>) =>
      dispatch(setTags<TagColumnNames>('main', tags)),
    setTake: (take: number) => dispatch(setTake('main', take)),
    setOrder: (order: OrderValue) => dispatch(setOrder('main', order)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Usage);
